import { useState, useRef, useEffect } from 'react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import useAuthStore from '../../store/authStore';
import Layout from '../../components/Layout';
import { toAssetUrl } from '../../utils/assetBase';
import { isFreePlan } from '../../utils/planAccess';
import { fmtDate, fmtTime, fmtDateTime, todayISO } from '../../utils/dateFormat';
import { printBill, getReceiptPrinterName } from '../../utils/printService';

// ── Shared helpers (same as Receipt.jsx) ─────────────────────────────────────
const WEBVIEW_PRINT_TIMEOUT_MS = 15000;

const toMoney    = (v) => Number(v || 0);
const formatMoney = (v) => `${toMoney(v).toFixed(2)} ₼`;
const trimQty    = (qty, d = 3) => Number(qty || 0).toFixed(d).replace(/\.?0+$/, '');

const formatQty = (qty, saleType) => {
    if ((saleType || 'piece') === 'piece') return `${Math.round(Number(qty || 0))}əd`;
    if (saleType === 'weight') return `${trimQty(qty)}kq`;
    if (saleType === 'liter')  return `${trimQty(qty)}lt`;
    return trimQty(qty);
};

const getReceiptTitle = (u) =>
    u?.tenant_plan === 'pro' ? (u?.tenant_name || 'Fiscal POS') : 'Fiscal POS';

const DEFAULT_RECEIPT_LOGO = `${window.location.origin}/FiscalPOS_black.png`;

const getReceiptLogoUrl = (u) => {
    if (isFreePlan(u?.tenant_plan)) return null;
    if (u?.receipt_logo_url) return toAssetUrl(u.receipt_logo_url);
    return DEFAULT_RECEIPT_LOGO;
};

// Set komponentlərini (set_group_id) tək sətrə qruplaşdır — əsl qəbzdəki kimi.
// Banket komponentləri (is_banquet_component) onsuz da ayrıca idarə olunur → çıxarılır.
const groupSetItems = (items) => {
    const groups = new Map();
    const list = [];
    for (const it of (items || [])) {
        if (Number(it.is_banquet_component)) continue;
        if (it.set_group_id) {
            let g = groups.get(it.set_group_id);
            if (!g) {
                const setName = String(it.note || 'Set').replace(/^(Set|Banket):\s*/i, '').split(' • ')[0];
                g = { _isSet: true, id: `set_${it.set_group_id}`, item_name: setName, sale_type: 'piece', note: null, _total: 0, _minCount: Infinity };
                groups.set(it.set_group_id, g);
                list.push(g);
            }
            g._total += Number(it.qty) * Number(it.unit_price);
            const unit = Number(it.set_unit_qty || 0);
            const cnt = unit > 0 ? Number(it.qty) / unit : 1;
            if (cnt < g._minCount) g._minCount = cnt;
        } else {
            list.push(it);
        }
    }
    for (const g of list) {
        if (g._isSet) {
            g.qty = Math.max(1, Math.round(g._minCount === Infinity ? 1 : g._minCount));
            g.unit_price = g.qty > 0 ? g._total / g.qty : 0;
        }
    }
    return list;
};

const buildReceiptText = (order, u) => {
    const subtotal       = toMoney(order?.subtotal);
    const servicePercent = toMoney(order?.service_percent);
    const serviceAmount  = toMoney(order?.service_amount);
    const discountAmount = toMoney(order?.discount_amount);
    const appliedDeposit = toMoney(order?.applied_deposit);
    const grandTotal     = toMoney(order?.grand_total || subtotal + serviceAmount - discountAmount + appliedDeposit);
    const payment        = order?.payment;

    const lines = [
        getReceiptTitle(u),
        'Musteri ceki (Yeniden cap)',
        '-------------------------------',
        `Telefon: ${order?.tenant_phone || '-'}`,
        `Masa: ${order?.table_name || '-'}`,
        `Sifaris: #${order?.id || '-'}`,
        `Ofisiant: ${order?.waiter_name || '-'}`,
        `Tarix: ${fmtDateTime(new Date())}`,
        '-------------------------------',
    ];

    groupSetItems(order?.items).forEach((item) => {
        const itemTotal = parseFloat(item.unit_price) * Number(item.qty);
        lines.push(item.item_name);
        lines.push(`  ${formatQty(item.qty, item.sale_type)} x ${parseFloat(item.unit_price).toFixed(2)} = ${itemTotal.toFixed(2)} AZN`);
        if (item.note && !item._isSet) lines.push(`  Qeyd: ${item.note}`);
    });

    lines.push('-------------------------------');
    lines.push(`Cem: ${subtotal.toFixed(2)} AZN`);
    lines.push(`${trimQty(servicePercent, 2)}% Servis Haqqi: ${serviceAmount.toFixed(2)} AZN`);
    lines.push(`Depozit: ${appliedDeposit.toFixed(2)} AZN`);
    lines.push(`Endirim: ${discountAmount.toFixed(2)} AZN`);
    lines.push(`Umumi cem: ${grandTotal.toFixed(2)} AZN`);

    if (payment) {
        lines.push('-------------------------------');
        if (payment.method === 'hesab') {
            lines.push(`Nagd: ${toMoney(payment.cash_amount).toFixed(2)} AZN`);
            lines.push(`Kart: ${toMoney(payment.card_amount).toFixed(2)} AZN`);
        } else {
            lines.push(`Odenis novu: ${payment.method === 'kart' ? 'Kart' : payment.method === 'delivery' ? 'Food delivery' : 'Nagd'}`);
            lines.push(`Odenilib: ${toMoney(payment.amount).toFixed(2)} AZN`);
        }
    }

    if (order?.note) {
        lines.push('-------------------------------');
        lines.push(`Umumi qeyd: ${order.note}`);
    }

    lines.push('');
    lines.push('Tesekkur edirik!');
    lines.push('Powered by www.fiscalbusiness.net');
    return lines.join('\n');
};

const hasWebViewBridge = () => Boolean(window.chrome?.webview?.postMessage);

const sendWebViewPrintRequest = (message) => new Promise((resolve, reject) => {
    if (!hasWebViewBridge()) {
        reject(new Error('Bu funksiya yalnız Windows tətbiqində işləyir.'));
        return;
    }
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const timeoutId = window.setTimeout(() => {
        window.chrome.webview.removeEventListener('message', handler);
        reject(new Error('Çap cavabı gözlənilən vaxtda gəlmədi.'));
    }, WEBVIEW_PRINT_TIMEOUT_MS);

    const handler = (event) => {
        const p = event?.data;
        if (!p || p.type !== 'print-bill-result' || p.requestId !== requestId) return;
        window.clearTimeout(timeoutId);
        window.chrome.webview.removeEventListener('message', handler);
        p.ok ? resolve(p) : reject(new Error(p.error || 'Çap baş tutmadı.'));
    };
    window.chrome.webview.addEventListener('message', handler);
    window.chrome.webview.postMessage({ ...message, requestId });
});

// ── Main component ────────────────────────────────────────────────────────────
export default function ReprintQaime() {
    const { user } = useAuthStore();
    const inputRef  = useRef(null);

    const [orderNum, setOrderNum] = useState('');
    const [order,    setOrder]    = useState(null);
    const [loading,  setLoading]  = useState(false);
    const [printing, setPrinting] = useState(false);
    // Maya (cost) — admin bu qaimənin maya dəyərini görsün
    const [mayaOpen,    setMayaOpen]    = useState(false);
    const [mayaData,    setMayaData]    = useState(null);
    const [mayaLoading, setMayaLoading] = useState(false);
    // Çek çap tarixçəsi (müştəri + yarışma çekləri, çap vaxtları) — bağlanmış qaimə üçün də
    const [logOpen,    setLogOpen]    = useState(false);
    const [logRows,    setLogRows]    = useState(null);
    const [logLoading, setLogLoading] = useState(false);

    // Gün üzrə qaimə siyahısı
    const [filterDate, setFilterDate] = useState(todayISO());
    const [dayOrders,  setDayOrders]  = useState([]);
    const [dayLoading, setDayLoading] = useState(false);

    const yesterdayISO = () => { const d = new Date(); d.setDate(d.getDate() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

    const loadByDate = async (date) => {
        setDayLoading(true);
        try {
            const res = await api.get('/orders/by-date', { params: { date } });
            setDayOrders(res.data.data || []);
        } catch (err) {
            toast.error('Siyahı yüklənmədi.');
            setDayOrders([]);
        } finally {
            setDayLoading(false);
        }
    };

    useEffect(() => { loadByDate(filterDate); }, [filterDate]);

    // Siyahıdan qaiməni aç (tam sifarişi çək → önizləmə + çap)
    const openOrder = async (id) => {
        setLoading(true);
        setOrder(null);
        try {
            const res = await api.get(`/orders/${id}`);
            setOrder(res.data.data);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            toast.error(err.response?.data?.msg || `#${id} tapılmadı.`);
        } finally {
            setLoading(false);
        }
    };

    // Yeni qaimə açılanda maya + çap tarixçəsi sıfırlansın
    useEffect(() => { setMayaOpen(false); setMayaData(null); setLogOpen(false); setLogRows(null); }, [order?.id]);

    // "YYYY-MM-DD HH:mm:ss" (Baku) → "DD-MM-YYYY saat HH:mm"
    const fmtLogTime = (s) => {
        if (!s) return '';
        const [d, t] = String(s).split(' ');
        if (!d) return String(s);
        const [y, m, day] = d.split('-');
        return `${day}-${m}-${y} saat ${(t || '').slice(0, 5)}`;
    };

    const toggleLog = async () => {
        const next = !logOpen;
        setLogOpen(next);
        if (!next || !order?.id) return;
        setLogLoading(true);
        try {
            const res = await api.get(`/orders/${order.id}/print-log`);
            setLogRows(res.data.data || []);
        } catch (e) {
            toast.error(e.response?.data?.msg || 'Çap tarixçəsi alınmadı.');
            setLogOpen(false);
        } finally {
            setLogLoading(false);
        }
    };

    const toggleMaya = async () => {
        const next = !mayaOpen;
        setMayaOpen(next);
        if (!next || !order?.id) return;
        setMayaLoading(true);
        try {
            const res = await api.get(`/orders/${order.id}/maya`);
            setMayaData(res.data);
        } catch (e) {
            toast.error(e.response?.data?.msg || 'Maya alınmadı.');
            setMayaOpen(false);
        } finally {
            setMayaLoading(false);
        }
    };

    const search = async (e) => {
        e?.preventDefault();
        const num = orderNum.trim().replace(/^#/, '');
        if (!num) { toast.error('Sifariş nömrəsi daxil edin.'); return; }
        setLoading(true);
        setOrder(null);
        try {
            const res = await api.get(`/orders/${num}`);
            setOrder(res.data.data);
        } catch (err) {
            toast.error(err.response?.data?.msg || `#${num} tapılmadı.`);
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = async () => {
        if (!order?.items?.length) { toast.error('Sifarişdə məhsul yoxdur.'); return; }
        setPrinting(true);
        try {
            const subtotal       = toMoney(order.subtotal);
            const servicePercent = toMoney(order.service_percent);
            const serviceAmount  = toMoney(order.service_amount);
            const discountAmount = toMoney(order.discount_amount);
            const appliedDeposit = toMoney(order.applied_deposit);
            const grandTotal     = toMoney(order.grand_total || subtotal + serviceAmount - discountAmount + appliedDeposit);

            const receiptPrinter = await getReceiptPrinterName();
            await printBill(receiptPrinter, {
                title:          getReceiptTitle(user),
                receiptLogoUrl: getReceiptLogoUrl(user),
                phone:          order.tenant_phone || '',
                orderId:        order.id,
                tableName:      order.table_name,
                waiterName:     order.waiter_name,
                subtotal,
                servicePercent,
                serviceAmount,
                discountAmount,
                depositAmount:  toMoney(order.deposit_amount),
                appliedDeposit,
                grandTotal,
                total:          grandTotal,
                note:           order.note || null,
                receiptText:    buildReceiptText(order, user),
                items: groupSetItems(order.items).map((item) => ({
                    name:      item.item_name,
                    qty:       Number(item.qty),
                    qtyLabel:  formatQty(item.qty, item.sale_type),
                    unitPrice: Number(item.unit_price),
                    saleType:  item.sale_type || 'piece',
                    note:      item._isSet ? null : (item.note || null),
                    total:     parseFloat(item.unit_price) * Number(item.qty),
                })),
            });
            toast.success('Qəbz printerə göndərildi.');
        } catch (err) {
            toast.error(err.message || 'Çap baş tutmadı.');
        } finally {
            setPrinting(false);
        }
    };

    // Receipt data
    const subtotal       = toMoney(order?.subtotal);
    const servicePercent = toMoney(order?.service_percent);
    const serviceAmount  = toMoney(order?.service_amount);
    const discountAmount = toMoney(order?.discount_amount);
    const appliedDeposit = toMoney(order?.applied_deposit);
    const grandTotal     = toMoney(order?.grand_total || subtotal + serviceAmount - discountAmount + appliedDeposit);
    const payment        = order?.payment || null;
    const paid           = toMoney(payment?.amount || grandTotal);
    const cashAmount     = toMoney(payment?.cash_amount);
    const cardAmount     = toMoney(payment?.card_amount);
    const change         = payment?.method === 'nagd' ? paid - grandTotal : 0;
    const methodNames    = { nagd: 'Nağd', kart: 'Kart', hesab: 'Hesab bölündü', delivery: 'Food delivery' };
    const methodIcons    = { nagd: '💵', kart: '💳', hesab: '🧾', delivery: '🛵' };

    return (
        <Layout>
            <div className="max-w-lg mx-auto">
                {/* Title */}
                <div className="mb-6">
                    <h1 className="text-xl font-black text-gray-800">🖨️ Qaimə yenidən çap</h1>
                    <p className="text-sm text-gray-400 mt-0.5">Tarix seçib qaiməni siyahıdan, və ya nömrə ilə axtarıb çap edin</p>
                </div>

                {/* Search bar */}
                <form onSubmit={search} className="flex gap-2 mb-6">
                    <div className="relative flex-1">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">#</span>
                        <input
                            ref={inputRef}
                            type="number"
                            min="1"
                            value={orderNum}
                            onChange={e => setOrderNum(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && search()}
                            placeholder="96"
                            autoFocus
                            className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl text-lg font-bold focus:outline-none focus:border-blue-500 transition-all"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-3 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-all"
                    >
                        {loading ? '⏳' : '🔍 Axtar'}
                    </button>
                </form>

                {/* Loading state */}
                {loading && (
                    <div className="py-16 text-center text-gray-400">
                        <div className="text-3xl mb-2 animate-pulse">🔍</div>
                        <p className="text-sm">Axtarılır...</p>
                    </div>
                )}

                {/* Empty state */}
                {/* Gün üzrə qaimə siyahısı (sifariş seçilməyibsə) */}
                {!loading && !order && (
                    <div>
                        {/* Tarix filteri */}
                        <div className="mb-4">
                            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Tarix</label>
                            <div className="flex flex-wrap items-center gap-2">
                                <button type="button" onClick={() => setFilterDate(todayISO())}
                                    className={`rounded-xl px-4 py-2 text-xs font-bold transition-all ${filterDate === todayISO() ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                                    Bugün
                                </button>
                                <button type="button" onClick={() => setFilterDate(yesterdayISO())}
                                    className={`rounded-xl px-4 py-2 text-xs font-bold transition-all ${filterDate === yesterdayISO() ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                                    Dünən
                                </button>
                                <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)}
                                    className="border-2 border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-blue-400" />
                            </div>
                        </div>

                        {dayLoading && (
                            <div className="py-12 text-center text-gray-400"><div className="text-3xl mb-2 animate-pulse">🧾</div><p className="text-sm">Yüklənir...</p></div>
                        )}

                        {!dayLoading && dayOrders.length === 0 && (
                            <div className="py-12 text-center text-gray-300">
                                <div className="text-5xl mb-3">🧾</div>
                                <p className="text-sm text-gray-400">Bu tarixdə bağlanmış qaimə yoxdur.</p>
                            </div>
                        )}

                        {!dayLoading && dayOrders.length > 0 && (
                            <div className="space-y-2">
                                <div className="text-xs text-gray-400 mb-1">{dayOrders.length} qaimə · ən son üstdə</div>
                                {dayOrders.map(o => (
                                    <button key={o.id} type="button" onClick={() => openOrder(o.id)}
                                        className="w-full text-left rounded-xl border border-gray-200 bg-white px-4 py-3 hover:border-blue-400 hover:bg-blue-50/40 transition-all">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-gray-800">#{o.id}</span>
                                                <span className="text-xs text-gray-500">🪑 {o.table_name || '—'}</span>
                                                <span className="text-xs text-gray-500">👤 {o.waiter_name || '—'}</span>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-mono text-base font-bold text-gray-800">{formatMoney(o.grand_total)}</div>
                                                <div className="text-[10px] text-gray-400">{fmtTime(o.closed_at)} · {fmtDate(o.closed_at)}</div>
                                            </div>
                                        </div>
                                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
                                            <span>Cəm: <b className="text-gray-700">{formatMoney(o.subtotal)}</b></span>
                                            {o.service_percent > 0 && <span>Servis {trimQty(o.service_percent, 2)}%: <b className="text-gray-700">{formatMoney(o.service_amount)}</b></span>}
                                            {o.nagd > 0 && <span>💵 Nağd: <b className="text-green-600">{formatMoney(o.nagd)}</b></span>}
                                            {o.pos  > 0 && <span>💳 POS: <b className="text-blue-600">{formatMoney(o.pos)}</b></span>}
                                            {o.delivery > 0 && <span>🛵 Delivery: <b className="text-orange-600">{formatMoney(o.delivery)}</b></span>}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Receipt preview */}
                {!loading && order && (
                    <div>
                        {/* Siyahıya qayıt */}
                        <button type="button" onClick={() => { setOrder(null); setOrderNum(''); }}
                            className="mb-3 inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 transition-all">
                            ← Siyahıya qayıt
                        </button>
                        {/* Print button at top */}
                        <div className="mb-4 flex items-center justify-between">
                            <div>
                                <span className="text-sm font-bold text-gray-700">Sifariş #{order.id}</span>
                                <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full ${
                                    order.status === 'baglanmis'
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-amber-100 text-amber-700'
                                }`}>
                                    {order.status === 'baglanmis' ? '✅ Ödənilmiş' : '⏳ Açıq'}
                                </span>
                            </div>
                            <button
                                onClick={handlePrint}
                                disabled={printing}
                                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold disabled:opacity-50 transition-all"
                            >
                                {printing ? '⏳ Göndərilir...' : '🖨️ Çap et'}
                            </button>
                        </div>

                        {/* Receipt card */}
                        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">

                            {/* Header */}
                            <div className="border-b border-dashed border-gray-200 px-6 py-5 text-center">
                                <div className="text-2xl mb-1">🧾</div>
                                <div className="text-base font-bold text-gray-800">{getReceiptTitle(user)}</div>
                                <div className="text-xs text-gray-400 mt-0.5">Yenidən çap · Sifariş qəbzi</div>
                            </div>

                            {/* Meta */}
                            <div className="border-b border-dashed border-gray-200 px-6 py-4">
                                <div className="grid grid-cols-2 gap-3">
                                    {[
                                        { label: 'Qəbz №',   value: `#${order.id}` },
                                        { label: 'Tarix',    value: payment?.paid_at ? fmtDate(payment.paid_at) : fmtDate(order.closed_at || new Date()) },
                                        { label: 'Masa',     value: order.table_name || '—' },
                                        { label: 'Ofisiant', value: order.waiter_name || '—' },
                                    ].map(m => (
                                        <div key={m.label}>
                                            <div className="mb-0.5 text-xs font-semibold uppercase tracking-wider text-gray-400">{m.label}</div>
                                            <div className="text-sm font-semibold text-gray-700">{m.value}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Items */}
                            <div className="border-b border-dashed border-gray-200 px-6 py-4">
                                <div className="mb-2 flex gap-2 border-b border-gray-100 pb-2 text-xs font-semibold uppercase text-gray-400">
                                    <span className="flex-1">Məhsul</span>
                                    <span className="w-12 text-center">Miq</span>
                                    <span className="w-16 text-right">Cəmi</span>
                                </div>
                                {groupSetItems(order.items).map(item => (
                                    <div key={item.id} className="flex gap-2 border-b border-gray-50 py-1.5 last:border-0">
                                        <div className="flex-1">
                                            <div className="text-sm text-gray-800">{item._isSet ? `🎁 ${item.item_name}` : item.item_name}</div>
                                            {item.note && !item._isSet && <div className="text-xs text-amber-500">📝 {item.note}</div>}
                                        </div>
                                        <span className="w-12 text-center font-mono text-xs text-gray-400">
                                            {formatQty(item.qty, item.sale_type)}
                                        </span>
                                        <span className="w-16 text-right font-mono text-sm font-bold text-gray-700">
                                            {(parseFloat(item.unit_price) * Number(item.qty)).toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {/* Totals */}
                            <div className="border-b border-dashed border-gray-200 px-6 py-4">
                                {[
                                    { label: 'Cəm',                                value: formatMoney(subtotal) },
                                    { label: `${trimQty(servicePercent,2)}% servis`, value: formatMoney(serviceAmount) },
                                    { label: 'Depozit',                            value: formatMoney(appliedDeposit) },
                                    { label: 'Endirim',                            value: formatMoney(discountAmount) },
                                ].map(r => (
                                    <div key={r.label} className="mb-1 flex justify-between text-sm text-gray-500">
                                        <span>{r.label}</span>
                                        <span className="font-mono">{r.value}</span>
                                    </div>
                                ))}
                                <div className="flex items-center justify-between border-t border-gray-200 pt-2 mt-2">
                                    <span className="font-bold text-gray-800">Ümumi cəm</span>
                                    <span className="font-mono text-xl font-bold text-gray-800">{formatMoney(grandTotal)}</span>
                                </div>

                                {payment && (
                                    <div className="mt-2 space-y-1">
                                        <div className="flex justify-between text-sm text-gray-500">
                                            <span>Ödənildi</span>
                                            <span className="font-mono">{formatMoney(paid)}</span>
                                        </div>
                                        {payment.method === 'hesab' && (
                                            <>
                                                <div className="flex justify-between text-sm text-gray-500">
                                                    <span>Nağd</span>
                                                    <span className="font-mono">{formatMoney(cashAmount)}</span>
                                                </div>
                                                <div className="flex justify-between text-sm text-gray-500">
                                                    <span>Kart</span>
                                                    <span className="font-mono">{formatMoney(cardAmount)}</span>
                                                </div>
                                            </>
                                        )}
                                        {change > 0.005 && (
                                            <div className="flex justify-between text-sm font-semibold text-green-600">
                                                <span>Qaytarıldı</span>
                                                <span className="font-mono">{formatMoney(change)}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Payment method */}
                            <div className="flex items-center justify-between border-b border-dashed border-gray-200 px-6 py-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-xl">{methodIcons[payment?.method] || '💵'}</span>
                                    <span className="text-sm font-semibold text-gray-700">
                                        {methodNames[payment?.method] || 'Nağd'}
                                    </span>
                                </div>
                                <span className="text-xs text-gray-400">
                                    {payment?.paid_at ? fmtTime(payment.paid_at) : ''}
                                </span>
                            </div>

                            {/* Footer */}
                            <div className="px-6 py-5 text-center">
                                {order.note && <div className="mb-2 text-xs text-gray-400">📝 {order.note}</div>}
                                <div className="text-xs text-gray-300">fiscalbusiness.net</div>
                            </div>
                        </div>

                        {/* Maya (cost) — yalnız admin görür (qəbz kartından kənarda, çapa düşməz) */}
                        <div className="mt-4">
                            <button
                                type="button"
                                onClick={toggleMaya}
                                className="w-full rounded-xl border border-dashed border-gray-300 py-2.5 text-xs font-bold text-gray-600 transition-all hover:bg-gray-50"
                            >
                                {mayaOpen ? '🧮 Mayanı gizlət' : '🧮 Bu qaimənin mayası'}
                            </button>
                            {mayaOpen && (
                                <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs">
                                    {mayaLoading ? (
                                        <div className="py-2 text-center text-gray-400">Hesablanır...</div>
                                    ) : mayaData ? (
                                        <>
                                            <div className="space-y-1">
                                                {mayaData.items.map((it, i) => (
                                                    <div key={i} className="flex justify-between text-gray-500">
                                                        <span className="truncate pr-2">{it.name} × {trimQty(it.qty, 3)}</span>
                                                        <span className="whitespace-nowrap font-mono">{formatMoney(it.line_cost)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="mt-2 flex justify-between border-t border-gray-200 pt-2 font-bold text-gray-800">
                                                <span>Maya (cəmi)</span>
                                                <span className="font-mono">{formatMoney(mayaData.total_cost)}</span>
                                            </div>
                                            <div className="mt-1 flex justify-between font-bold text-emerald-700">
                                                <span>Mənfəət (satış − maya)</span>
                                                <span className="font-mono">{formatMoney(grandTotal - Number(mayaData.total_cost || 0))}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="py-2 text-center text-gray-400">Məlumat yoxdur.</div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Çek çap tarixçəsi — bu qaimənin çekləri (müştəri + yarışma) nə zaman çıxıb */}
                        <div className="mt-3">
                            <button
                                type="button"
                                onClick={toggleLog}
                                className="w-full rounded-xl border border-dashed border-gray-300 py-2.5 text-xs font-bold text-gray-600 transition-all hover:bg-gray-50"
                            >
                                {logOpen ? '🧾 Çek çap tarixçəsini gizlət' : '🧾 Çek çap tarixçəsi (çap vaxtları)'}
                            </button>
                            {logOpen && (
                                <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs">
                                    {logLoading ? (
                                        <div className="py-2 text-center text-gray-400">Yüklənir...</div>
                                    ) : (logRows && logRows.length > 0) ? (
                                        <div className="space-y-1.5">
                                            {logRows.map((r, i) => (
                                                <div key={r.id} className="flex items-center justify-between gap-2">
                                                    <div className="flex min-w-0 items-center gap-2">
                                                        <span className="font-mono text-gray-400">{i + 1}.</span>
                                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.type === 'gift' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                                            {r.type === 'gift' ? '🎁 Yarışma çeki' : '🧾 Müştəri çeki'}
                                                        </span>
                                                        {r.printed_by_name && <span className="truncate text-gray-400">· {r.printed_by_name}</span>}
                                                    </div>
                                                    <span className="whitespace-nowrap text-gray-500">{fmtLogTime(r.printed_at)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="py-2 text-center text-gray-400">Bu qaimə üçün çek çap qeydi yoxdur.</div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Bottom print button */}
                        <button
                            onClick={handlePrint}
                            disabled={printing}
                            className="mt-4 w-full py-3 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-50 hover:-translate-y-0.5"
                            style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                        >
                            {printing ? '⏳ Çap olunur...' : '🖨️ Qəbzi çap et'}
                        </button>
                    </div>
                )}
            </div>
        </Layout>
    );
}
